import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Typography,
  InputAdornment,
  Snackbar,
  Checkbox,
  Popover,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { inventoryService, locationService } from '../services'
import CachedImage from '../components/CachedImage'
import ListQueryToolbar from '../components/shared/ListQueryToolbar'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import TableEmptyState from '../components/shared/TableEmptyState'
import CsvDownloadDialog from '../components/shared/CsvDownloadDialog'
import ViewInventoryLotDialog from '../components/inventory-management/ViewInventoryLotDialog'
import { useInventoryList } from '../hooks/useInventoryList'
import { useListQueryState } from '../hooks/useListQueryState'
import {
  AVAILABLE_INVENTORY_CSV_COLUMNS,
  buildInventoryCsvContent,
  buildInventoryDetailPath,
  buildInventoryManageSearchParams,
  getAvailableQuantity,
  getInventoryManageReturnPath,
  formatInventoryDateTime,
  INVENTORY_MANAGE_DEFAULT_LOCATION,
  inventoryManageHasExplicitParams,
  parseInventoryManageSearchParams,
  toInventoryApiParams,
  validateInventoryQuantities,
} from '../utils/inventoryManagementUtils'
import { downloadCsvFile } from '../utils/productManagementUtils'

const LOCATION_PAGE_LIMIT = 50
const INVENTORY_ROUTE_KEY = 'inventory.manage'

const formatInventoryAmount = (value) => {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const parseInventoryFilters = (sp) => parseInventoryManageSearchParams(sp)

const buildInventoryFilters = (filters) => buildInventoryManageSearchParams(filters)

const inventoryLabelFromFilters = (filters) => {
  const parts = []
  if ((filters.q || '').trim()) parts.push(`“${filters.q.trim()}”`)
  const codes = (filters.locations || [])
    .map((l) => (typeof l === 'string' ? l : l?.code))
    .filter(Boolean)
  if (codes.length) parts.push(codes.join(', '))
  return parts.length ? parts.join(' · ') : 'Inventory filters'
}

const inventoryIsDefaultFilters = (filters) => {
  const qEmpty = !(filters.q || '').trim()
  const codes = (filters.locations || [])
    .map((l) => (typeof l === 'string' ? l : l?.code))
    .filter(Boolean)
  const defaultLoc =
    codes.length === 1 && codes[0] === INVENTORY_MANAGE_DEFAULT_LOCATION
  return qEmpty && defaultLoc && (filters.page || 0) === 0 && (filters.limit || 10) === 10
}

const InventoryManagementPage = () => {
  const {
    filters,
    setFilters,
    resetFilters,
    applyRecent,
    recent,
    isDefault,
  } = useListQueryState({
    routeKey: INVENTORY_ROUTE_KEY,
    parse: parseInventoryFilters,
    build: buildInventoryFilters,
    hasExplicitParams: inventoryManageHasExplicitParams,
    labelFromFilters: inventoryLabelFromFilters,
    isDefaultFilters: inventoryIsDefaultFilters,
  })

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewItem, setViewItem] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const [selectedItem, setSelectedItem] = useState(null)
  const [editFormData, setEditFormData] = useState({})
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  // CSV Download Dialog State
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)
  const [csvDownloading, setCsvDownloading] = useState(false)
  const [csvLimit, setCsvLimit] = useState(100)
  const [csvDownloadAll, setCsvDownloadAll] = useState(false)

  const [selectedColumns, setSelectedColumns] = useState(
    AVAILABLE_INVENTORY_CSV_COLUMNS.map((col) => col.id),
  )

  const [locationOptions, setLocationOptions] = useState([])
  const [locationsPage, setLocationsPage] = useState(1)
  const [locationsHasMore, setLocationsHasMore] = useState(true)
  const [locationsLoading, setLocationsLoading] = useState(false)
  const [locationsError, setLocationsError] = useState(null)
  const [locationSearchInput, setLocationSearchInput] = useState('')
  const [locationSearch, setLocationSearch] = useState('')
  const [locationMenuAnchor, setLocationMenuAnchor] = useState(null)
  const isLocationMenuOpen = Boolean(locationMenuAnchor)

  const selectedLocations = useMemo(
    () => filters.locations || [],
    [filters.locations],
  )
  const selectedLocationCodes = useMemo(
    () => selectedLocations.map((l) => l.code),
    [selectedLocations],
  )

  const editQuantityError = useMemo(
    () => validateInventoryQuantities(editFormData.quantity_on_hand, editFormData.reserved_quantity),
    [editFormData.quantity_on_hand, editFormData.reserved_quantity],
  )

  const editAvailableQuantity = useMemo(
    () => getAvailableQuantity(editFormData.quantity_on_hand, editFormData.reserved_quantity),
    [editFormData.quantity_on_hand, editFormData.reserved_quantity],
  )

  const {
    inventory,
    loading,
    error,
    setError,
    page,
    rowsPerPage,
    totalCount,
    fetchInventory,
  } = useInventoryList(filters)

  const applySearchQuery = useCallback(
    (q) => {
      setFilters({ q, page: 0 }, { commitRecent: true })
    },
    [setFilters],
  )

  const handleInventoryPageChange = useCallback(
    (_event, newPage) => {
      setFilters({ page: newPage })
    },
    [setFilters],
  )

  const handleInventoryRowsPerPageChange = useCallback(
    (event) => {
      setFilters({
        page: 0,
        limit: parseInt(event.target.value, 10) || 10,
      })
    },
    [setFilters],
  )

  const loadLocationsPage = useCallback(async (pageNum, searchTerm, mode) => {
    try {
      setLocationsLoading(true)
      setLocationsError(null)
      const res = await locationService.getLocations({
        page: pageNum,
        limit: LOCATION_PAGE_LIMIT,
        search: searchTerm || undefined,
      })
      const items = res.items || []
      const hasNext = res.pagination?.hasNextPage ?? false
      setLocationOptions((prev) => (mode === 'replace' ? items : [...prev, ...items]))
      setLocationsPage(pageNum)
      setLocationsHasMore(hasNext)
    } catch (err) {
      console.error('Error loading locations:', err)
      setLocationsError(err.response?.data?.message || 'Failed to load locations')
    } finally {
      setLocationsLoading(false)
    }
  }, [])

  useEffect(() => {
    const handle = setTimeout(() => {
      setLocationSearch(locationSearchInput.trim())
    }, 300)
    return () => clearTimeout(handle)
  }, [locationSearchInput])

  useEffect(() => {
    loadLocationsPage(1, locationSearch, 'replace')
  }, [locationSearch, loadLocationsPage])

  const handleLocationListScroll = (e) => {
    const el = e.currentTarget
    if (locationsLoading || !locationsHasMore) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadLocationsPage(locationsPage + 1, locationSearch, 'append')
    }
  }

  const toggleLocation = (loc) => {
    const upper = String(loc.code || '').toUpperCase()
    const has = selectedLocations.some(
      (l) => String(l.code || '').toUpperCase() === upper,
    )
    const next = has
      ? selectedLocations.filter(
          (l) => String(l.code || '').toUpperCase() !== upper,
        )
      : [...selectedLocations, { code: loc.code, name: loc.name || null }]
    setFilters({ locations: next, page: 0 }, { commitRecent: true })
  }

  const removeSelectedLocation = (code) => {
    const upper = String(code || '').toUpperCase()
    const next = selectedLocations.filter(
      (l) => String(l.code || '').toUpperCase() !== upper,
    )
    setFilters({ locations: next, page: 0 }, { commitRecent: true })
  }

  const resetLocationsToDefault = () => {
    setFilters(
      {
        locations: [{ code: INVENTORY_MANAGE_DEFAULT_LOCATION, name: null }],
        page: 0,
      },
      { commitRecent: true },
    )
  }

  const clearAllLocations = () => {
    setFilters({ locations: [], page: 0 }, { commitRecent: true })
  }

  const openLocationMenu = (e) => setLocationMenuAnchor(e.currentTarget)
  const closeLocationMenu = () => setLocationMenuAnchor(null)

  // Copy article code (inventory sku field)
  const handleCopySku = async (sku) => {
    try {
      await navigator.clipboard.writeText(sku)
      setToastMessage('Article Code copied to clipboard!')
      setToastOpen(true)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = sku
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
        setToastMessage('Article Code copied to clipboard!')
        setToastOpen(true)
      } catch (err) {
        console.error('Failed to copy article code:', err)
        setToastMessage('Failed to copy Article Code')
        setToastOpen(true)
      }
      document.body.removeChild(textArea)
    }
  }

  const handleViewClick = (item) => {
    setViewItem(item)
    setViewDialogOpen(true)
  }

  const handleViewClose = () => {
    setViewDialogOpen(false)
    setViewItem(null)
  }

  const inventoryReturnPath = getInventoryManageReturnPath(
    buildInventoryManageSearchParams(filters),
  )

  const inventoryDetailTo = (item) =>
    buildInventoryDetailPath(item.id, inventoryReturnPath)

  const inventoryDetailState = (item) => ({
    sku: item.sku,
    productId:
      item.admin_product_id || item.product_id || item.adminProduct?.id,
    productName: item.adminProduct?.product_name,
    returnTo: inventoryReturnPath,
  })

  // Edit Inventory
  const handleEditClick = (item) => {
    setSelectedItem(item)
    setEditFormData({
      quantity_on_hand: item.quantity_on_hand,
      minimum_stock_level: item.minimum_stock_level || 0,
      maximum_stock_level: item.maximum_stock_level || 0,
      reorder_point: item.reorder_point || 0,
      reserved_quantity: item.reserved_quantity || 0,
      average_cost: item.average_cost || 0,
      location: item.location || '',
      notes: item.notes || '',
    })
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setSelectedItem(null)
    setEditFormData({})
    setActionError(null)
  }

  const handleEditChange = (field, value) => {
    let parsedValue;

    // Integer fields
    if (['minimum_stock_level', 'maximum_stock_level', 'reorder_point', 'quantity_on_hand'].includes(field)) {
      parsedValue = parseInt(value, 10) || 0;
    }
    // Float fields  
    else if (['average_cost'].includes(field)) {
      parsedValue = parseFloat(value) || 0;
    }
    // Integer fields for reserved_quantity
    else if (['reserved_quantity'].includes(field)) {
      parsedValue = parseInt(value, 10) || 0;
    }
    // String fields
    else {
      parsedValue = value;
    }

    setEditFormData({
      ...editFormData,
      [field]: parsedValue,
    })
  }

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      if (!selectedItem || !selectedItem.id) {
        setActionError('Inventory lot ID is required for update')
        return
      }

      const quantityError = validateInventoryQuantities(
        editFormData.quantity_on_hand,
        editFormData.reserved_quantity,
      )
      if (quantityError) {
        setActionError(quantityError)
        return
      }

      // Use the lot UUID to target the exact inventory record
      await inventoryService.updateAdminInventoryByLotId(selectedItem.id, {
        ...editFormData,
        reason: 'Manual update from inventory management',
        metadata: {
          source: 'admin_inventory_management',
          inventory_id: selectedItem.id,
          sku: selectedItem.sku,
          admin_product_id:
            selectedItem.admin_product_id ||
            selectedItem.adminProduct?.id ||
            selectedItem.product_id,
          updated_at: new Date().toISOString()
        }
      })

      setSuccessMessage('Inventory updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)

      handleEditClose()
      fetchInventory()
    } catch (err) {
      console.error('Error updating inventory:', err)
      setActionError(err.response?.data?.message || 'Failed to update inventory')
    } finally {
      setActionLoading(false)
    }
  }

  // Delete Inventory
  const handleDeleteClick = (item) => {
    setSelectedItem(item)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      const response = await inventoryService.deleteInventory(selectedItem.id)

      // Check if deletion was actually successful
      if (response && response.success) {
        setSuccessMessage('Inventory deleted successfully')
        setTimeout(() => setSuccessMessage(null), 3000)

        setDeleteConfirmOpen(false)
        setSelectedItem(null)
        fetchInventory()
      } else {
        setActionError(response?.message || 'Unknown error occurred')
      }
    } catch (err) {
      console.error('Error deleting inventory:', err)
      const errorData = err.response?.data

      if (err.response?.status === 409) {
        setActionError(errorData?.message || 'Cannot delete inventory. Related records exist.')
      } else {
        setActionError(errorData?.message || 'Failed to delete inventory')
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setSelectedItem(null)
  }

  // CSV Download Dialog Handlers
  const handleOpenCsvDialog = () => {
    setCsvDialogOpen(true)
    setCsvDownloadAll(false)
    setCsvLimit(100)
  }

  const handleCloseCsvDialog = () => {
    setCsvDialogOpen(false)
    setCsvDownloading(false)
  }

  const handleToggleAllColumns = useCallback(() => {
    setSelectedColumns((prev) =>
      prev.length === AVAILABLE_INVENTORY_CSV_COLUMNS.length
        ? []
        : AVAILABLE_INVENTORY_CSV_COLUMNS.map((col) => col.id),
    )
  }, [])

  const handleToggleColumn = useCallback((columnId) => {
    setSelectedColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    )
  }, [])

  const handleCsvDownloadAllChange = useCallback((checked) => {
    setCsvDownloadAll(checked)
    setCsvLimit(checked ? 0 : 100)
  }, [])

  const handleCsvLimitChange = useCallback((value) => {
    const parsed = parseInt(value, 10) || 0
    setCsvLimit(parsed > 0 ? parsed : 100)
  }, [])

  const handleDownloadCSV = useCallback(async () => {
    if (selectedColumns.length === 0) {
      setActionError('Please select at least one column to download')
      return
    }

    try {
      setCsvDownloading(true)
      setActionError(null)

      const params = {
        ...toInventoryApiParams({ ...filters, page: 0 }),
        page: 1,
        limit: csvDownloadAll ? 0 : csvLimit,
      }

      const response = await inventoryService.getInventory(params)
      const inventoryToExport = response.items || response.data || []

      if (inventoryToExport.length === 0) {
        setActionError('No inventory found to download')
        return
      }

      const csv = buildInventoryCsvContent(inventoryToExport, selectedColumns)
      downloadCsvFile(csv, `inventory-${new Date().toISOString().split('T')[0]}.csv`)

      setCsvDialogOpen(false)
      setSuccessMessage(`Successfully downloaded ${inventoryToExport.length} inventory item(s)`)
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      console.error('Error downloading CSV:', err)
      setActionError(err.response?.data?.message || 'Failed to download CSV')
    } finally {
      setCsvDownloading(false)
    }
  }, [selectedColumns, csvDownloadAll, csvLimit, filters])

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            Inventory Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            View, edit, and manage inventory levels across all products
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleOpenCsvDialog}
          disabled={totalCount === 0}
          sx={{
            color: '#e90000',
            borderColor: '#e90000',
            '&:hover': { bgcolor: '#E8F5E9' },
            mt: 2,
          }}
        >
          Download CSV
        </Button>
      </Box>

      <ListQueryToolbar
        label="Search by Article Code"
        placeholder="Enter Article Code to search inventory..."
        appliedSearch={filters.q || ''}
        onApplySearchQuery={applySearchQuery}
        onClearFilters={resetFilters}
        isDefault={isDefault}
        recent={recent}
        onApplyRecent={applyRecent}
        mb={2}
        maxWidth={720}
      >
        <Button
          type="button"
          variant="outlined"
          onClick={openLocationMenu}
          startIcon={<FilterListIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            textTransform: 'none',
            color: '#263238',
            borderColor: '#E5E7EB',
            bgcolor: '#F9FAFB',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#F3F4F6',
              borderColor: '#E5E7EB',
            },
          }}
        >
          Locations
          {selectedLocationCodes.length > 0 ? ` (${selectedLocationCodes.length})` : ''}
        </Button>
      </ListQueryToolbar>

      {/* Selected location chips */}
      {selectedLocations.length > 0 && (
        <Box sx={{ mt: -1, mb: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: '#666', mr: 0.5 }}>
              Filtering by:
            </Typography>
            {selectedLocations.map((l) => (
              <Chip
                key={l.code}
                label={l.name ? `${l.name} (${l.code})` : l.code}
                onDelete={() => removeSelectedLocation(l.code)}
                size="small"
                sx={{
                  bgcolor: '#e90000',
                  color: '#FFFFFF',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255,255,255,0.85)',
                    '&:hover': { color: '#FFFFFF' },
                  },
                }}
              />
            ))}
            <Button
              size="small"
              onClick={clearAllLocations}
              sx={{ textTransform: 'none', color: '#666', minWidth: 'auto' }}
            >
              Clear all
            </Button>
          </Box>
        )}

      {/* Location filter popover */}
      <Popover
        open={isLocationMenuOpen}
        anchorEl={locationMenuAnchor}
        onClose={closeLocationMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 340,
              borderRadius: 2,
              boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
            },
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#263238' }}>
              Filter by location
            </Typography>
            <Button
              size="small"
              onClick={resetLocationsToDefault}
              sx={{ textTransform: 'none', color: '#e90000', minWidth: 'auto' }}
            >
              Reset to {INVENTORY_MANAGE_DEFAULT_LOCATION}
            </Button>
          </Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by code or name..."
            value={locationSearchInput}
            onChange={(e) => setLocationSearchInput(e.target.value)}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: locationSearchInput && (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={() => setLocationSearchInput('')}
                    sx={{ minWidth: 'auto', p: 0.5, color: '#666' }}
                  >
                    Clear
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          {locationsError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {locationsError}
            </Alert>
          )}
          <Box
            onScroll={handleLocationListScroll}
            sx={{
              maxHeight: 280,
              overflowY: 'auto',
              border: '1px solid #E5E7EB',
              borderRadius: 1,
              bgcolor: '#FFFFFF',
            }}
          >
            {locationOptions.length === 0 && !locationsLoading && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  No locations found
                </Typography>
              </Box>
            )}
            {locationOptions.map((loc) => {
              const upper = String(loc.code || '').toUpperCase()
              const selected = selectedLocationCodes.some(
                (c) => String(c || '').toUpperCase() === upper
              )
              const label = loc.name ? `${loc.name} (${loc.code})` : loc.code
              return (
                <Box
                  key={loc.id ?? loc.code}
                  onClick={() => toggleLocation(loc)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    bgcolor: selected ? 'rgba(233,0,0,0.04)' : 'transparent',
                    '&:hover': { bgcolor: selected ? 'rgba(233,0,0,0.08)' : '#F3F4F6' },
                  }}
                >
                  <Checkbox
                    checked={selected}
                    size="small"
                    onChange={() => toggleLocation(loc)}
                    sx={{
                      p: 0.25,
                      color: '#D1D5DB',
                      '&.Mui-checked': { color: '#e90000' },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: '#263238', flex: 1, fontWeight: selected ? 500 : 400 }}
                  >
                    {label}
                  </Typography>
                </Box>
              )
            })}
            {locationsLoading && (
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={20} sx={{ color: '#e90000' }} />
              </Box>
            )}
            {!locationsHasMore && locationOptions.length > 0 && !locationsLoading && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  py: 1,
                }}
              >
                End of list
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {selectedLocationCodes.length} selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                onClick={clearAllLocations}
                disabled={selectedLocationCodes.length === 0}
                sx={{ textTransform: 'none', color: '#666' }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={closeLocationMenu}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#e90000',
                  '&:hover': { bgcolor: '#c40000' },
                }}
              >
                Done
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>

      {/* Messages */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Inventory List */}
      {/* Inventory Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Article Code</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Barcode</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }} align="right">
                  On Hand
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }} align="right">
                  Selling Price
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }} align="right">
                  MRP
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Last Updated</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={11} />
              ) : inventory.length === 0 ? (
                <TableEmptyState colSpan={11} message="No inventory items found" />
              ) : (
                inventory.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    '&:hover': { bgcolor: '#f8f9fb' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell>
                    <Box
                      component={RouterLink}
                      to={inventoryDetailTo(item)}
                      state={inventoryDetailState(item)}
                      sx={{ display: 'inline-flex', textDecoration: 'none' }}
                    >
                      <CachedImage
                        src={item.adminProduct?.image_url_portrait ||
                          item.adminProduct?.image_url_banner ||
                          item.adminProduct?.image_url_logo ||
                          (item.adminProduct?.additional_images && item.adminProduct.additional_images.length > 0 ? item.adminProduct.additional_images[0] : null)
                        }
                        alt={item.adminProduct?.product_name}
                        size={40}
                        variant="rounded"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {item.sku}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopySku(item.sku)}
                        title="Copy Article Code"
                        sx={{
                          color: '#666',
                          padding: '2px',
                          '&:hover': {
                            color: '#e90000',
                            backgroundColor: 'rgba(233, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box
                      component={RouterLink}
                      to={inventoryDetailTo(item)}
                      state={inventoryDetailState(item)}
                      sx={{
                        color: 'inherit',
                        textDecoration: 'none',
                        '&:hover': { color: '#1976d2' },
                      }}
                    >
                      {item.adminProduct?.product_name || 'Unknown Product'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#666' }}>{item.adminProduct?.brand || '-'}</TableCell>
                  <TableCell sx={{ color: '#666', fontSize: '0.85rem' }}>
                    {item.adminProduct?.barcode || '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#e90000' }}>
                    {typeof item.quantity_on_hand === 'number' ? Math.floor(item.quantity_on_hand) : item.quantity_on_hand}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatInventoryAmount(item.adminProduct?.cost_price)}
                  </TableCell>
                  <TableCell align="right">
                    {formatInventoryAmount(item.adminProduct?.price)}
                  </TableCell>
                  <TableCell sx={{ color: '#666' }}>{item.location || '-'}</TableCell>
                  <TableCell sx={{ color: '#666', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {formatInventoryDateTime(item.updated_at)}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'nowrap' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewClick(item)}
                        title="View Details"
                        sx={{ color: '#e90000' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(item)}
                        title="Edit"
                        sx={{ color: '#1976d2' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(item)}
                        title="Delete"
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleInventoryPageChange}
          onRowsPerPageChange={handleInventoryRowsPerPageChange}
        />
      </Card>

      {/* Edit Inventory Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>Edit Inventory</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Quantity On Hand"
              type="number"
              value={editFormData.quantity_on_hand || ''}
              onChange={(e) => handleEditChange('quantity_on_hand', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ min: '0', step: '1' }}
              error={Boolean(editQuantityError)}
              helperText={editQuantityError || `Available: ${editAvailableQuantity}`}
            />

            <TextField
              label="Minimum Stock Level"
              type="number"
              value={editFormData.minimum_stock_level || ''}
              onChange={(e) => handleEditChange('minimum_stock_level', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ min: '0', step: '1' }}
            />

            <TextField
              label="Maximum Stock Level"
              type="number"
              value={editFormData.maximum_stock_level || ''}
              onChange={(e) => handleEditChange('maximum_stock_level', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ min: '0', step: '1' }}
            />

            <TextField
              label="Reorder Point"
              type="number"
              value={editFormData.reorder_point || ''}
              onChange={(e) => handleEditChange('reorder_point', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ min: '0', step: '1' }}
            />

            <TextField
              label="Reserved Quantity"
              type="number"
              value={editFormData.reserved_quantity || ''}
              onChange={(e) => handleEditChange('reserved_quantity', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ min: '0', step: '1', max: String(editFormData.quantity_on_hand || 0) }}
              error={Boolean(editQuantityError)}
              helperText={
                editQuantityError
                  ? editQuantityError
                  : `Must not exceed quantity on hand (${editFormData.quantity_on_hand || 0})`
              }
            />

            <TextField
              label="Average Cost"
              type="number"
              value={editFormData.average_cost || ''}
              onChange={(e) => handleEditChange('average_cost', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ min: '0', step: '0.01' }}
            />

            <TextField
              label="Location"
              value={editFormData.location || ''}
              onChange={(e) => handleEditChange('location', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              placeholder="e.g., A-12-C, Warehouse-1"
            />

            <TextField
              label="Notes"
              value={editFormData.notes || ''}
              onChange={(e) => handleEditChange('notes', e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              disabled={actionLoading}
              placeholder="Additional inventory notes"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button onClick={handleEditClose} disabled={actionLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={actionLoading || Boolean(editQuantityError)}
            sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ViewInventoryLotDialog
        open={viewDialogOpen}
        item={viewItem}
        onClose={handleViewClose}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#fff3e0' }}>Delete Inventory?</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            Are you sure you want to delete inventory for Article Code <strong>{selectedItem?.sku}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={actionLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={actionLoading}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <CsvDownloadDialog
        open={csvDialogOpen}
        appliedSearchQuery={filters.q || ''}
        totalCount={totalCount}
        downloading={csvDownloading}
        error={actionError}
        csvDownloadAll={csvDownloadAll}
        csvLimit={csvLimit}
        selectedColumns={selectedColumns}
        availableColumns={AVAILABLE_INVENTORY_CSV_COLUMNS}
        downloadAllLabel="Download All Inventory"
        downloadAllDescription="Download all inventory items matching your current search (no limit)"
        limitLabel="Number of Items to Download"
        resultsUnit="item(s)"
        onClose={handleCloseCsvDialog}
        onDownload={handleDownloadCSV}
        onClearError={() => setActionError(null)}
        onCsvDownloadAllChange={handleCsvDownloadAllChange}
        onCsvLimitChange={handleCsvLimitChange}
        onToggleAllColumns={handleToggleAllColumns}
        onToggleColumn={handleToggleColumn}
      />
    </Box>
  )
}

export default InventoryManagementPage
